import { uploadData } from "aws-amplify/storage";
import errorHandler from "common/errorHandler";
import { getArrayBufferFromFileObject } from "common/fileHandler";
import _ from "lodash";
import parsePath from "parse-filepath";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { Endpoints } from "services/api";
import { v4 as uuidv4 } from "uuid";
import * as actions from "./actions";

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

export function* createDataObjectsUploadJob(api, tenantId, job) {
    let result = yield call([api, api.post], `${Endpoints.jobs}/api/v1/jobs`, job, { tenantid: tenantId });
    return result.data;
}

function* getDataObjectsByDataSetId(api, tenantId, businessAreaId, schemaId, dataSetId, pageKey) {
    let params = {};
    if (pageKey) {
        params.pageKey = pageKey;
    }
    let result = yield call(
        [api, api.get],
        `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects`,
        { tenantid: tenantId },
        {
            params,
        },
    );
    return result.data;
}

function* getDataObject(api, tenantId, businessAreaId, schemaId, dataSetId, dataObjectId) {
    let response = yield call(
        [api, api.get],
        `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}`,
        { tenantid: tenantId },
    );
    return response.data;
}

function* createDataObject(api, tenantId, businessAreaId, schemaId, dataSetId, dataObject) {
    let response = yield call([api, api.post], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects`, dataObject, {
        tenantid: tenantId,
    });
    return response.data;
}

function* updateDataObject(api, tenantId, businessAreaId, schemaId, dataSetId, dataObjectId, dataObject) {
    yield call([api, api.put], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}`, dataObject, {
        tenantid: tenantId,
    });
}

function* deleteDataObject(api, businessAreaId, schemaId, dataSetId, dataObjectId) {
    yield call([api, api.delete], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}`, {});
}

export function* getDataObjectPermission(api, tenantId, userId) {
    let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${userId}/permissions/resources/dataobjects`, { tenantid: tenantId });
    return result.data;
}

function* getDataObjectByUUID(api, dataObjectId) {
    try {
        let response = yield call([api, api.get], `${Endpoints.datasets}/api/v1/dataobjects/${dataObjectId}`);
        return response.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* getDataObjectSharedLink(api, businessAreaId, schemaId, dataSetId, dataObjectId, readOnly, expires) {
    let response = yield call(
        [api, api.get],
        `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}/shareurl?readonly=${readOnly}&expires=${expires}`,
    );
    return response.data;
}

function* getSharedDataObjectByUUID(api, tenantId, userId, readOnly, expiresAt, signatureDate, signature, dataObjectId) {
    try {
        let response = yield call(
            [api, api.getAnonymous],
            `${Endpoints.datasets}/api/v1/shared/dataobjects/${dataObjectId}`,
            {},
            {
                params: {
                    tenant: tenantId,
                    user: userId,
                    readonly: readOnly,
                    expires: expiresAt,
                    date: signatureDate,
                    signature: signature,
                },
            },
        );
        return response.data;
    } catch (error) {
        if (error.isAxiosError) {
            if (error.response && error.response.status === 404) {
                return null;
            }
        }
        throw error;
    }
}

function* updateSharedDataObject(api, tenantId, userId, readOnly, expiresAt, signatureDate, signature, businessAreaId, schemaId, dataSetId, dataObjectId, dataObject) {
    yield call(
        [api, api.putAnonymous],
        `${Endpoints.datasets}/api/v1/shared/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/${dataObjectId}`,
        dataObject,
        {},
        {
            params: {
                tenant: tenantId,
                user: userId,
                readonly: readOnly,
                expires: expiresAt,
                date: signatureDate,
                signature: signature,
            },
        },
    );
}

function* getUnmaskedDataByUUID(api, dataObjectId) {
    const endpoint = `${Endpoints.datasets}/api/v1/dataobjects/${dataObjectId}/unmask`;
    let response = yield call([api, api.get], endpoint);
    return response.data;
}

function* searchDataSetDataObjects(api, businessAreaId, schemaId, dataSetId, query) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}/dataobjects/search`, {
        query,
    });
    return result.data;
}

function buildAiSuggestPayload({ businessAreaId, schemaId, dataSet, fileReadResult, schemaModel }) {
    const columns = fileReadResult?.columns && fileReadResult.columns.length > 0 ? fileReadResult.columns : fileReadResult?.meta?.fields || [];

    const rows = Array.isArray(fileReadResult?.data) ? fileReadResult.data.slice(0, 100) : [];

    const sourceColumns = columns.map((colName) => {
        const samples = [];
        for (let i = 0; i < rows.length && samples.length < 100; i++) {
            const v = rows[i] ? rows[i][colName] : undefined;
            if (v !== undefined) samples.push(v);
        }
        return {
            name: colName,
            samples,
        };
    });

    const schemaAttributes = Array.isArray(schemaModel?.fields)
        ? schemaModel.fields.map((f) => ({
              id: f.fieldId,
              name: f.name,
              description: f.description,
              datatype: f.dataType,
              tags: f.tags || [],
              isPrimary: !!f.isPrimary,
              isRequired: !!f.isRequired,
              validation: f.validation || null,
          }))
        : [];

    return {
        businessAreaId,
        schemaId,
        dataSetId: dataSet?.dataSetId,
        schema: {
            id: schemaId,
            attributes: schemaAttributes,
        },
        source: {
            columns: sourceColumns,
        },
        meta: {
            fileName: fileReadResult?.file?.name || null,
            connectionType: fileReadResult?.file?.connectionType || null,
            tableName: fileReadResult?.file?.tableName || null,
        },
    };
}

function* postAiMappingSuggest(api, tenantId, url, payload, headers = {}) {
    const response = yield call([api, api.post], url, payload, {
        ...headers,
    });
    return response.data;
}

export function* getAiMappingSuggestionRequest(api, { businessAreaId, schemaId, dataSet, fileReadResult, schemaModel }) {
    try {
        const tenant = yield select(getTenant);

        const payload = buildAiSuggestPayload({ businessAreaId, schemaId, dataSet, fileReadResult, schemaModel });

        const url = `${Endpoints.datasets}/api/v1/ai/mappings/suggest`;

        const result = yield call(postAiMappingSuggest, api, tenant.tenantId, url, payload);

        yield put(actions.getAiMappingSuggestionSuccess(result));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "AI Mapping Suggestion");
        yield put(actions.getAiMappingSuggestionFailure(errorObject));
    }
}

function* postDatasetDescribe(api, payload) {
    const response = yield call([api, api.post], `${Endpoints.schemas}/api/v1/ai/dataset/describe`, payload);
    return response.data;
}

function* getAiJobStatus(api, jobId) {
    const response = yield call([api, api.get], `${Endpoints.schemas}/api/v1/ai/jobs/${jobId}`);
    return response.data;
}

function* waitForAiJobCompletion(api, jobId, featureLabel) {
    const maxPollCount = 40;
    const pollIntervalMs = 3000;

    for (let pollCount = 0; pollCount < maxPollCount; pollCount++) {
        let statusResult = null;
        try {
            statusResult = yield call(getAiJobStatus, api, jobId);
        } catch (error) {
            if (!(error?.isAxiosError && error?.response?.status === 404)) {
                throw error;
            }
        }

        if (statusResult?.status === "COMPLETED") {
            return statusResult;
        }

        if (statusResult?.status === "FAILED") {
            throw new Error(statusResult?.errorMessage || `${featureLabel} generation failed.`);
        }

        yield delay(pollIntervalMs);
    }

    throw new Error(`${featureLabel} generation is taking longer than expected. Please check again in a moment.`);
}

export function* getDatasetDescriptionRequest(api, { businessAreaId, schemaId, dataSetId, regenerate }) {
    try {
        const jobResult = yield call(postDatasetDescribe, api, {
            businessAreaId,
            schemaId,
            dataSetId,
            regenerate: regenerate === true,
        });
        const completedResult = yield call(waitForAiJobCompletion, api, jobResult.jobId, "Dataset description");
        const base = completedResult.result || completedResult.computed || {};
        const reportHtml = completedResult.reportHtml || base.reportHtml || null;
        yield put(actions.getDatasetDescriptionSuccess({ ...base, reportHtml }));
    } catch (error) {
        const errorObject = errorHandler(error, "Generate", "dataset description");
        yield put(actions.getDatasetDescriptionFailure(errorObject));
    }
}

export function* createDataObjectsUploadJobRequest(api, { businessAreaId, schemaId, dataSet, dataUploadOptions, file, fieldMappings }) {
    try {
        let tenant = yield select(getTenant);
        let user = yield select(getUser);
        if (file) {
            let jobInfo = {
                jobIdentifier: dataSet.dataSetId,
                runUniqueJob: true,
                businessAreaId,
                schemaId,
                dataSetId: dataSet.dataSetId,
                tenantId: tenant.tenantId,
                userId: user.userId,
                fileName: file.name,
                uploadOptions: dataUploadOptions,
                fieldMappings,
            };
            if (!file.connectionId) {
                let fileName = `tenants/${tenant.tenantId}/users/${user.userId}/dataSets/${dataSet.dataSetId}/dataobjects/${uuidv4()}${parsePath(file.name).ext}`;
                let arrayBuffer = yield call(getArrayBufferFromFileObject, file);
                let uploadTask = uploadData({
                    path: `public/${fileName}`,
                    data: arrayBuffer,
                    options: {
                        contentType: file.type,
                    },
                });
                let result = yield call(() => uploadTask.result);
                jobInfo = {
                    ...jobInfo,
                    s3: process.env.REACT_APP_BUCKET_NAME,
                    key: `public/${fileName}`,
                    region: process.env.REACT_APP_BUCKET_REGION,
                };
            } else {
                jobInfo = {
                    ...jobInfo,
                    connectionId: file.connectionId,
                    connectionType: file.connectionType,
                    tableName: file.tableName,
                };
                if (file.connectionType === "aws_s3") {
                    jobInfo = {
                        ...jobInfo,
                        s3: file.bucket,
                        key: file.path,
                    };
                }
            }
            let dataObjectsUploadJob = {
                name: `Data upload ${dataSet.dataSetName}`,
                description: `Data upload ${dataSet.dataSetName}`,
                businessAreaId: businessAreaId,
                recordType: "DATA_OBJECTS",
                schemaId,
                dataSetId: dataSet.dataSetId,
                jobInfo: jobInfo,
                jobType: "DATA_OBJECTS_BULK_UPLOAD",
            };
            yield call(createDataObjectsUploadJob, api, tenant.tenantId, dataObjectsUploadJob);
        }

        yield put(actions.createDataObjectsUploadJobSuccess());
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "data objects upload job");
        yield put(actions.createDataObjectsUploadJobFailure(errorObject));
    }
}

export function* getDataObjectsByDataSetIdRequest(api, { businessAreaId, schemaId, dataSetId }) {
    try {
        let tenant = yield select(getTenant);
        let dataObjectsResult = yield call(getDataObjectsByDataSetId, api, tenant.tenantId, businessAreaId, schemaId, dataSetId);
        yield put(actions.getDataObjectsByDataSetIdSuccess(dataObjectsResult));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Objects");
        yield put(actions.getDataObjectsByDataSetIdFailure(errorObject));
    }
}

export function* getDataObjectsByDataSetIdNextPageRequest(api, { businessAreaId, schemaId, dataSetId, pageKey }) {
    try {
        let tenant = yield select(getTenant);
        let dataObjectsResult = yield call(getDataObjectsByDataSetId, api, tenant.tenantId, businessAreaId, schemaId, dataSetId, pageKey);
        yield put(actions.getDataObjectsByDataSetIdNextPageSuccess(dataObjectsResult));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Objects");
        yield put(actions.getDataObjectsByDataSetIdNextPageFailure(errorObject));
    }
}

export function* getDataObjectRequest(api, { businessAreaId, schemaId, dataSetId, dataObjectId }) {
    try {
        let tenant = yield select(getTenant);
        let dataObject = yield call(getDataObject, api, tenant.tenantId, businessAreaId, schemaId, dataSetId, dataObjectId);
        yield put(actions.getDataObjectSuccess(dataObject));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Object");
        // A record deduplication merged into another (PRE-2837): say which, not just "not found".
        if (errorObject.serverCode === "DATA_OBJECT_MERGED" && errorObject.serverMessage) {
            errorObject.message = errorObject.serverMessage;
            errorObject.survivor = error.response && error.response.data ? error.response.data.survivor : undefined;
        }
        // Which record could not be read, for a screen waiting on one in particular.
        yield put(actions.getDataObjectFailure({ ...errorObject, dataObjectId }));
    }
}

export function* createDataObjectRequest(api, { businessAreaId, schemaId, dataSetId, dataObject }) {
    try {
        let tenant = yield select(getTenant);
        let newDataObject = yield call(createDataObject, api, tenant.tenantId, businessAreaId, schemaId, dataSetId, dataObject);
        yield put(actions.createDataObjectSuccess(newDataObject));
    } catch (error) {
        let errorObject = errorHandler(error, "Create", "Data Object");
        yield put(actions.createDataObjectFailure(errorObject));
    }
}

export function* updateDataObjectRequest(api, { businessAreaId, schemaId, dataSetId, dataObjectId, dataObject }) {
    try {
        let tenant = yield select(getTenant);
        yield call(updateDataObject, api, tenant.tenantId, businessAreaId, schemaId, dataSetId, dataObjectId, dataObject);
        dataObject.version = dataObject.version + 1;
        yield put(actions.updateDataObjectSuccess(dataObjectId, dataObject));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "Data Object");
        yield put(actions.updateDataObjectFailure(errorObject));
    }
}

export function* deleteDataObjectRequest(api, { businessAreaId, schemaId, dataSetId, dataObjectId }) {
    try {
        yield call(deleteDataObject, api, businessAreaId, schemaId, dataSetId, dataObjectId);
        yield put(actions.deleteDataObjectSuccess(businessAreaId, schemaId, dataSetId, dataObjectId));
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "Data Object");
        yield put(actions.deleteDataObjectFailure(errorObject));
    }
}

export function* getDataObjectPermissionRequest(api) {
    try {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        let tenant = yield select(getTenant);
        let user = yield select(getUser);
        let permissionResponse = yield call(getDataObjectPermission, api, tenant.tenantId, user.userId);
        if (permissionResponse && permissionResponse.Items && permissionResponse.Items.length > 0) {
            let permissionActions = permissionResponse.Items.map((item) => item.actions);
            permissionActions = _.union(...permissionActions);
            if (permissionActions.length > 0) {
                if (permissionActions.includes("add")) {
                    permission.canAdd = true;
                }
                if (permissionActions.includes("view")) {
                    permission.canView = true;
                }
                if (permissionActions.includes("edit")) {
                    permission.canEdit = true;
                }
                if (permissionActions.includes("delete")) {
                    permission.canDelete = true;
                }
            }
        }

        yield put(actions.getDataObjectPermissionSuccess(permission));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "user dataobjects permission");
        yield put(actions.getDataObjectPermissionFailure(errorObject));
    }
}

export function* getDataObjectByUUIDRequest(api, { dataObjectId }) {
    try {
        let result = yield call(getDataObjectByUUID, api, dataObjectId);
        yield put(actions.getDataObjectByUUIDSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Object");
        yield put(actions.getDataObjectByUUIDFailure(errorObject));
    }
}

export function* getDataObjectSharedLinkRequest(api, { businessAreaId, schemaId, dataSetId, dataObjectId, readOnly, expires }) {
    try {
        let result = yield call(getDataObjectSharedLink, api, businessAreaId, schemaId, dataSetId, dataObjectId, readOnly, expires);
        yield put(actions.getDataObjectSharedLinkSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Object share link");
        yield put(actions.getDataObjectSharedLinkFailure(errorObject));
    }
}

export function* getSharedDataObjectByUUIDRequest(api, { tenantId, userId, readOnly, expiresAt, signatureDate, signature, dataObjectId }) {
    try {
        let result = yield call(getSharedDataObjectByUUID, api, tenantId, userId, readOnly, expiresAt, signatureDate, signature, dataObjectId);
        yield put(actions.getDataObjectByUUIDSuccess(result));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "Data Object");
        yield put(actions.getDataObjectByUUIDFailure(errorObject));
    }
}

export function* updateSharedDataObjectRequest(
    api,
    { tenantId, userId, readOnly, expiresAt, signatureDate, signature, businessAreaId, schemaId, dataSetId, dataObjectId, dataObject },
) {
    try {
        yield call(updateSharedDataObject, api, tenantId, userId, readOnly, expiresAt, signatureDate, signature, businessAreaId, schemaId, dataSetId, dataObjectId, dataObject);
        dataObject.version = dataObject.version + 1;
        yield put(actions.updateDataObjectSuccess(dataObjectId, dataObject));
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "Data Object");
        yield put(actions.updateDataObjectFailure(errorObject));
    }
}

export function* getUnmaskedDataByUUIDRequest(api, action) {
    try {
        const { params } = action || {};
        if (!params || !params.dataObjectId) {
            throw new Error("dataObjectId is required to fetch unmasked data.");
        }

        const { dataObjectId } = params;

        const result = yield call(getUnmaskedDataByUUID, api, dataObjectId);
        yield put(actions.getUnmaskedDataByUUIDSuccess(result));
    } catch (error) {
        const errorObject = errorHandler(error, "Get", "Unmasked Data");
        yield put(actions.getUnmaskedDataByUUIDFailure(errorObject));
    }
}

export function* searchDataSetDataObjectsRequest(api, { businessAreaId, schemaId, dataSetId, query }) {
    try {
        let dataObjectsResult = yield call(searchDataSetDataObjects, api, businessAreaId, schemaId, dataSetId, query);
        yield put(actions.searchDataSetDataObjectsSuccess(dataObjectsResult));
    } catch (error) {
        let errorObject = errorHandler(error, "Search", "Data Objects");
        yield put(actions.searchDataSetDataObjectsFailure(errorObject));
    }
}

export function* watchCreateDataObjectsUploadJobRequest(api, { params }) {
    yield call(createDataObjectsUploadJobRequest, api, params);
}

export function* watchGetDataObjectsByDataSetIdRequest(api, { params }) {
    yield call(getDataObjectsByDataSetIdRequest, api, params);
}

export function* watchGetDataObjectsByDataSetIdNextPageRequest(api, { params }) {
    yield call(getDataObjectsByDataSetIdNextPageRequest, api, params);
}

export function* watchGetDataObjectRequest(api, { params }) {
    yield call(getDataObjectRequest, api, params);
}

export function* watchCreateDataObjectRequest(api, { params }) {
    yield call(createDataObjectRequest, api, params);
}

export function* watchUpdateDataObjectRequest(api, { params }) {
    yield call(updateDataObjectRequest, api, params);
}

export function* watchDeleteDataObjectRequest(api, { params }) {
    yield call(deleteDataObjectRequest, api, params);
}

export function* watchGetDataObjectPermissionRequest(api, { params }) {
    yield call(getDataObjectPermissionRequest, api, params);
}

export function* watchGetDataObjectByUUIDRequest(api, { params }) {
    yield call(getDataObjectByUUIDRequest, api, params);
}

export function* watchGetDataObjectSharedLinkRequest(api, { params }) {
    yield call(getDataObjectSharedLinkRequest, api, params);
}

export function* watchGetSharedDataObjectByUUIDRequest(api, { params }) {
    yield call(getSharedDataObjectByUUIDRequest, api, params);
}

export function* watchUpdateSharedDataObjectRequest(api, { params }) {
    yield call(updateSharedDataObjectRequest, api, params);
}

export function* watchGetUnmaskedDataByUUIDRequest(api, action) {
    yield call(getUnmaskedDataByUUIDRequest, api, action);
}

export function* watchSearchDataSetDataObjectsRequest(api, { params }) {
    yield call(searchDataSetDataObjectsRequest, api, params);
}

export function* watchGetAiMappingSuggestionRequest(api, { params }) {
    yield call(getAiMappingSuggestionRequest, api, params);
}

export function* watchGetDatasetDescriptionRequest(api, { params }) {
    yield call(getDatasetDescriptionRequest, api, params);
}

export default function* ({ api }) {
    yield takeLatest(actions.CREATE_DATA_OBJECTS_UPLOAD_JOB_REQUEST, watchCreateDataObjectsUploadJobRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECTS_BY_DATASET_ID_REQUEST, watchGetDataObjectsByDataSetIdRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECTS_BY_DATASET_ID_NEXT_PAGE_REQUEST, watchGetDataObjectsByDataSetIdNextPageRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECT_REQUEST, watchGetDataObjectRequest, api);
    yield takeLatest(actions.CREATE_DATA_OBJECT_REQUEST, watchCreateDataObjectRequest, api);
    yield takeLatest(actions.UPDATE_DATA_OBJECT_REQUEST, watchUpdateDataObjectRequest, api);
    yield takeLatest(actions.DELETE_DATA_OBJECT_REQUEST, watchDeleteDataObjectRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECT_PERMISSIONS_REQUEST, watchGetDataObjectPermissionRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECT_BY_UUID_REQUEST, watchGetDataObjectByUUIDRequest, api);
    yield takeLatest(actions.GET_DATA_OBJECT_SHARED_LINK_REQUEST, watchGetDataObjectSharedLinkRequest, api);
    yield takeLatest(actions.GET_SHARED_DATA_OBJECT_BY_UUID_REQUEST, watchGetSharedDataObjectByUUIDRequest, api);
    yield takeLatest(actions.UPDATE_SHARED_DATA_OBJECT_REQUEST, watchUpdateSharedDataObjectRequest, api);
    yield takeLatest(actions.GET_UNMASKED_DATA_BY_UUID_REQUEST, watchGetUnmaskedDataByUUIDRequest, api);
    yield takeLatest(actions.GET_AI_MAPPING_SUGGESTION_REQUEST, watchGetAiMappingSuggestionRequest, api);
    yield takeLatest(actions.GET_DATASET_DESCRIPTION_REQUEST, watchGetDatasetDescriptionRequest, api);
    yield takeLatest(actions.SEARCH_DATASET_DATA_OBJECTS_REQUEST, watchSearchDataSetDataObjectsRequest, api);
}
